import { useField } from 'formik';
import React, { FC } from 'react';
import DatePicker from 'react-datepicker';
import { enUS, sv } from 'date-fns/locale';
import { getDate } from 'date-fns';

// Models
import FormikError from '../FormikError';

// Components
import FormikDatePickerHeader from './FormikDatePickerHeader';

interface Props {
  name: string;
  label: string;
  locale: string | undefined;
  minDate?: Date;
  onChange?: (date: Date | null) => void;
  dateFilter?: (date: Date) => boolean;
}

const FormikDatePicker: FC<Props> = ({ name, label, minDate, locale, onChange, dateFilter }) => {
  const [field, meta, helpers] = useField<Date | null>(name);

  const renderDayContents = (_: number, date: Date) => {
    return <span>{getDate(date)}</span>;
  };

  const handleOnChange = (date: Date | null) => {
    if (onChange) {
      onChange(date);
    }
    helpers.setValue(date);
    // Hack to prevent submitting before field change has occured
    setTimeout(() => {
      helpers.setTouched(true);
    }, 1);
  };

  const handleOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setTimeout(() => {
      helpers.setTouched(true);
    }, 1);
  };

  const dateFnsLocale = locale === 'sv-SE' ? sv : enUS;

  return (
    <div className='aea-form-datepicker'>
      <label className='aea-form-datepicker__label' htmlFor={name}>
        {label}
      </label>
      <DatePicker
        locale={dateFnsLocale}
        selected={field.value}
        minDate={minDate}
        dateFormat='dd/MM/YYY'
        showYearDropdown
        filterDate={dateFilter}
        showPopperArrow={false}
        renderCustomHeader={(props) => <FormikDatePickerHeader locale={dateFnsLocale} {...props} />}
        renderDayContents={renderDayContents}
        onChange={handleOnChange}
        onBlur={handleOnBlur}
        popperModifiers={[
          {
            name: 'sameWidth',
            phase: 'beforeWrite',
            enabled: true,
            fn: ({ state }) => {
              state.styles.popper.width = `${state.rects.reference.width}px`;
            },
            effect({ state }) {
              state.elements.popper.style.width = `${
                // @ts-ignore
                state.elements.reference.offsetWidth
              }px`;
            },
            requires: ['computeStyles'],
          },
        ]}
      />
      {meta.touched && meta.error && <FormikError name={name} />}
    </div>
  );
};

export default FormikDatePicker;
